import { useEffect, useState } from 'react';

import FileDownloadIcon from '@mui/icons-material/GetApp';
import { Chip } from '@mui/material';
import { isEmpty } from 'lodash';
import {
  AutocompleteInput,
  Datagrid,
  DateField,
  FunctionField,
  Link,
  List,
  NumberField,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  ShowButton,
  TextField,
  TextInput,
  useGetIdentity,
  Button,
  FilterButton,
  TopToolbar,
  useUpdate,
  useNotify,
} from 'react-admin';
import { DataProviderResource } from 'src/constants';
import {
  AccountStatusDto,
  AccountTypeDto,
  OfferOrderListVisibleColumnDto,
  OfferOrderPaymentTypeDto,
  OfferOrderStatusDto,
  UpdateUserDashboardSettingsBodyDto,
} from 'src/dtos';
import { AccountStatusChip, OfferOrderStatusChip, MSelectColumnsButton } from 'src/lib';
import {
  AccountStatus,
  AccountStatusLabel,
  AccountTypeLabel,
  OfferOrder,
  OfferOrderPaymentTypeLabel,
  OfferOrderStatus,
  OfferOrderStatusLabel,
} from 'src/models';
import { getOfferOrderPaymentStatusColor, getCurrencyFormatter } from 'src/utils';

import { OfferOrderFileExportModal } from './components/OfferOrderFileExportModal';
import { modelFields } from './constants';
import { OfferOrderListEditActions } from './OfferOrderListEditActions';

const TOGGLEABLE_COLUMNS: Record<string, { key: string; label: string; name: OfferOrderListVisibleColumnDto }> = {
  clearingFirm: {
    key: 'transferDetails.clearingFirm',
    label: 'Clearing Firm',
    name: OfferOrderListVisibleColumnDto.ClearingFirm,
  },
  statementReceivedAt: {
    key: 'transferDetails.statementReceivedAt',
    label: 'Statement Received At',
    name: OfferOrderListVisibleColumnDto.StatementReceivedAt,
  },
  acatSentAt: {
    key: 'transferDetails.acatSentAt',
    label: 'ACAT Sent At',
    name: OfferOrderListVisibleColumnDto.AcatSentAt,
  },
  acatReceivedAt: {
    key: 'transferDetails.acatReceivedAt',
    label: 'ACAT Received At',
    name: OfferOrderListVisibleColumnDto.AcatReceivedAt,
  },
  acatSubmittedToExternalAt: {
    key: 'transferDetails.acatSubmittedToExternalAt',
    label: 'ACAT Submitted At',
    name: OfferOrderListVisibleColumnDto.AcatSubmittedToExternalAt,
  },
  acatResubmittedToExternalAt: {
    key: 'transferDetails.acatResubmittedToExternalAt',
    label: 'ACAT Resubmitted At',
    name: OfferOrderListVisibleColumnDto.AcatResubmittedToExternalAt,
  },
  rejectReason: {
    key: 'transferDetails.rejectReason',
    label: 'Reject Reason',
    name: OfferOrderListVisibleColumnDto.RejectReason,
  },
  notes: { key: 'notes', label: 'Notes', name: OfferOrderListVisibleColumnDto.Notes },
  representatives: {
    key: 'representatives',
    label: 'Representatives',
    name: OfferOrderListVisibleColumnDto.Representatives,
  },
};

const filterList = [
  <TextInput key='accountId' label='Account id' source='accountId' />,
  <TextInput key='accountNumber' label='Account number' source='accountNumber' alwaysOn />,
  <SelectInput
    key='type'
    label='Account type'
    source='accountType'
    choices={[
      { id: AccountTypeDto.Individual, name: AccountTypeLabel.Individual },
      { id: AccountTypeDto.Joint, name: AccountTypeLabel.Joint },
      { id: AccountTypeDto.Retirement, name: AccountTypeLabel.Retirement },
      { id: AccountTypeDto.Ugma, name: AccountTypeLabel.Ugma },
      { id: AccountTypeDto.Entity, name: AccountTypeLabel.Entity },
    ]}
  />,
  <SelectInput
    key='status'
    label='Account status'
    source='accountStatus'
    choices={[
      { id: AccountStatusDto.AccountSetup, name: AccountStatusLabel.AccountSetup },
      { id: AccountStatusDto.ActionRequired, name: AccountStatusLabel.ActionRequired },
      { id: AccountStatusDto.BackOffice, name: AccountStatusLabel.BackOffice },
      { id: AccountStatusDto.InvestigationSubmitted, name: AccountStatusLabel.InvestigationSubmitted },
      { id: AccountStatusDto.New, name: AccountStatusLabel.New },
      { id: AccountStatusDto.Pending, name: AccountStatusLabel.Pending },
      { id: AccountStatusDto.PendingSubmit, name: AccountStatusLabel.PendingSubmit },
      { id: AccountStatusDto.ReadyForBackOffice, name: AccountStatusLabel.ReadyForBackOffice },
      { id: AccountStatusDto.Complete, name: AccountStatusLabel.Complete },
      { id: AccountStatusDto.Suspended, name: AccountStatusLabel.Suspended },
      { id: AccountStatusDto.Canceled, name: AccountStatusLabel.Canceled },
      { id: AccountStatusDto.Error, name: AccountStatusLabel.Error },
      { id: AccountStatusDto.Rejected, name: AccountStatusLabel.Rejected },
    ]}
    optionText={(record: { id: AccountStatusDto; name: AccountStatusLabel }) => (
      <AccountStatusChip value={new AccountStatus(record.id)} />
    )}
  />,
  <SelectInput
    key='paymentType'
    source='paymentType'
    choices={[
      { id: OfferOrderPaymentTypeDto.Ach, name: OfferOrderPaymentTypeLabel.Ach },
      { id: OfferOrderPaymentTypeDto.Wire, name: OfferOrderPaymentTypeLabel.Wire },
      { id: OfferOrderPaymentTypeDto.Check, name: OfferOrderPaymentTypeLabel.Check },
      { id: OfferOrderPaymentTypeDto.Card, name: OfferOrderPaymentTypeLabel.Card },
      { id: OfferOrderPaymentTypeDto.Acat, name: OfferOrderPaymentTypeLabel.Acat },
    ]}
  />,
  <TextInput key='firstName' label='First name' source='firstName' />,
  <TextInput key='lastName' label='Last name' source='lastName' />,
  <TextInput key='email' label='Email' source='email' />,
  <ReferenceInput key='offerName' source='offerId' reference='offers' sort={{ field: 'name', order: 'ASC' }} alwaysOn>
    <AutocompleteInput label='Offer' optionText='name' fullWidth filterToQuery={searchText => ({ name: searchText })} />
  </ReferenceInput>,
  <SelectArrayInput
    key='status'
    label='Status'
    source='status'
    fullWidth
    choices={[
      { id: OfferOrderStatusDto.PendingFunds, name: OfferOrderStatusLabel.PendingFunds },
      { id: OfferOrderStatusDto.PendingOfferClose, name: OfferOrderStatusLabel.PendingOfferClose },
      { id: OfferOrderStatusDto.PendingAction, name: OfferOrderStatusLabel.PendingAction },
      { id: OfferOrderStatusDto.PendingFirmCancellation, name: OfferOrderStatusLabel.PendingFirmCancellation },
      { id: OfferOrderStatusDto.Approved, name: OfferOrderStatusLabel.Approved },
      { id: OfferOrderStatusDto.Complete, name: OfferOrderStatusLabel.Complete },
      { id: OfferOrderStatusDto.Cancelled, name: OfferOrderStatusLabel.Cancelled },
      { id: OfferOrderStatusDto.Rejected, name: OfferOrderStatusLabel.Rejected },
      { id: OfferOrderStatusDto.Deleted, name: OfferOrderStatusLabel.Deleted },
    ]}
    alwaysOn
    optionText={(record: { id: OfferOrderStatusDto; name: OfferOrderStatusLabel }) => (
      <OfferOrderStatusChip value={new OfferOrderStatus(record.id)} />
    )}
  />,
];

export const OfferOrderList = () => {
  const notify = useNotify();
  const { data: identity, refetch } = useGetIdentity();
  const [update] = useUpdate<UpdateUserDashboardSettingsBodyDto & { id: string }>();
  const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false);

  const visibleColumnsFromSettings: OfferOrderListVisibleColumnDto[] =
    identity?.dashboardSettings?.offerOrderList?.visibleColumns || [];

  const [columnOptions, setColumnOptions] = useState<{ key: string; label: string; visible: boolean; name?: string }[]>(
    Object.values(TOGGLEABLE_COLUMNS).map(col => ({
      key: col.key,
      label: col.label,
      visible: visibleColumnsFromSettings.includes(col.name),
      name: col.name,
    })),
  );

  const isColumnVisible = (columnKey: string): boolean => {
    return columnOptions.find(c => c.key === columnKey)?.visible ?? false;
  };

  useEffect(() => {
    setColumnOptions(
      Object.values(TOGGLEABLE_COLUMNS).map(col => ({
        key: col.key,
        label: col.label,
        visible: visibleColumnsFromSettings.includes(col.name),
        name: col.name,
      })),
    );
  }, [identity?.dashboardSettings]);

  return (
    <List
      title='Offer Orders'
      filters={filterList}
      sort={{ field: 'createdAt', order: 'DESC' }}
      emptyWhileLoading
      actions={
        <TopToolbar>
          <MSelectColumnsButton
            options={columnOptions}
            onChange={val => {
              setColumnOptions(val);

              if (identity) {
                const visibleColumns = val
                  .filter(c => c.visible && c.name)
                  .map(c => c.name as OfferOrderListVisibleColumnDto);
                update(
                  DataProviderResource.UpdateUserDashboardSettings,
                  {
                    id: identity.id.toString(),
                    data: {
                      offerOrderList: isEmpty(visibleColumns)
                        ? null
                        : {
                            visibleColumns,
                          },
                    },
                  },
                  {
                    onSuccess: () => {
                      refetch?.();
                    },
                    onError: (err: any) => {
                      notify(`Error saving visible columns: ${err.message}`, { type: 'error' });
                    },
                  },
                );
              }
            }}
          />
          <FilterButton />
          <Button label='Export' onClick={() => setIsExportDialogOpen(true)}>
            <FileDownloadIcon />
          </Button>
          <OfferOrderFileExportModal
            isOpen={isExportDialogOpen}
            onClose={() => setIsExportDialogOpen(false)}
            fields={modelFields}
          />
        </TopToolbar>
      }>
      <Datagrid bulkActionButtons={<OfferOrderListEditActions />}>
        <TextField source='id' label='Id' sortable={false} />
        <FunctionField
          source='offerName'
          label='Offer name'
          render={(record: OfferOrder) => {
            return <Link to={`/Offers/${record?.offerId}/show`}>{record?.offerName}</Link>;
          }}
        />
        <TextField source='account.primaryAccountHolder.fullName' label='Full name' sortable={false} />
        <TextField source='account.primaryAccountHolder.email' label='Email' />
        <TextField label='Account id' source='accountId' sortable={false} />
        <FunctionField
          label='Account number'
          render={(order: OfferOrder) => {
            if (!order?.account) {
              return null;
            }

            return <Link to={`/Accounts/${order.account.id}/show`}>{order.account.accountNumber}</Link>;
          }}
          sortable={false}
        />
        <TextField source='account.typeLabel' label='Account type' sortable={false} />
        <FunctionField
          label='Account status'
          render={(order: OfferOrder) => {
            if (order?.account.status) {
              return <AccountStatusChip value={order.account.status} />;
            }

            return null;
          }}
          sortable={false}
        />
        <FunctionField
          label='Payment type'
          render={(record: OfferOrder) => {
            if (!record) {
              return null;
            }

            if (!record.paymentType.value) {
              return 'NA';
            }

            if (record.paymentType.isCard || !isEmpty(record.refunds)) {
              return (
                <div>
                  <span style={{ marginRight: '4px' }}>{record.paymentType.label}</span>
                  <Chip
                    size='small'
                    label={record.paymentStatus.label}
                    color={getOfferOrderPaymentStatusColor(record.paymentStatus.value)}
                  />
                </div>
              );
            }

            return `${record.paymentType.label} ${
              record.retirementAccountFundingType?.value ? `(${record.retirementAccountFundingType.label})` : ''
            }`;
          }}
          sortable={false}
        />
        <FunctionField
          source='status'
          label='Status'
          render={(record: OfferOrder) => {
            if (!record) {
              return null;
            }

            return <OfferOrderStatusChip value={record.status} />;
          }}
          sortable
        />
        <FunctionField
          label='Total cost'
          render={(record: OfferOrder) => {
            if (!record) {
              return null;
            }

            return (
              <div>
                {getCurrencyFormatter().format(record.totalCost)}{' '}
                {record.conditional && `(${getCurrencyFormatter().format(record.conditional.totalInvestment)})`}
              </div>
            );
          }}
        />
        <NumberField
          source='cashBalance'
          label='Cash balance'
          options={{ style: 'currency', currency: 'USD' }}
          sortable={false}
        />
        <NumberField
          source='cashAvailable'
          label='Cash available'
          options={{ style: 'currency', currency: 'USD' }}
          sortable={false}
        />
        {isColumnVisible('transferDetails.clearingFirm') && (
          <TextField source='transferDetails.clearingFirm' label='Clearing Firm' sortable={false} />
        )}
        {isColumnVisible('transferDetails.statementReceivedAt') && (
          <DateField source='transferDetails.statementReceivedAt' label='Statement Received At' sortable={false} />
        )}
        {isColumnVisible('transferDetails.acatSentAt') && (
          <DateField source='transferDetails.acatSentAt' label='ACAT Sent At' sortable={false} />
        )}
        {isColumnVisible('transferDetails.acatReceivedAt') && (
          <DateField source='transferDetails.acatReceivedAt' label='ACAT Received At' sortable={false} />
        )}
        {isColumnVisible('transferDetails.acatSubmittedToExternalAt') && (
          <DateField source='transferDetails.acatSubmittedToExternalAt' label='ACAT Submitted At' sortable={false} />
        )}
        {isColumnVisible('transferDetails.acatResubmittedToExternalAt') && (
          <DateField
            source='transferDetails.acatResubmittedToExternalAt'
            label='ACAT Resubmitted At'
            sortable={false}
          />
        )}
        {isColumnVisible('transferDetails.rejectReason') && (
          <TextField source='transferDetails.rejectReason' label='Reject Reason' sortable={false} />
        )}
        {isColumnVisible('notes') && <TextField source='notes' label='Notes' sortable={false} />}
        {isColumnVisible('representatives') && (
          <FunctionField
            label='Representatives'
            render={(record: OfferOrder) => {
              return record.representatives?.map(rep => rep.firstName).join('/') ?? 'N/A';
            }}
            sortable={false}
          />
        )}
        <DateField source='createdAt' label='Created at' />
        <ShowButton label='Show' />
      </Datagrid>
    </List>
  );
};
